import { SdkMessageTypes } from '../constants';

/**
 * Represents the names of all SDK scenarios that can be created
 */
export type TelemetryScenarioName =
  | 'appInitialization'
  | 'appTermination'
  | 'preFetchAuthToken'
  | 'api.appInstallDialog.openAppInstallDialog'
  | 'api.authentication.notifyFailure'
  | 'api.authentication.notifySuccess'
  | 'api.appEntity.selectAppEntity'
  | 'api.calendar.composeMeeting'
  | 'api.calendar.openCalendarItem'
  | 'api.call.startCall'
  | 'api.chat.openConversation'
  | 'api.chat.closeConversation'
  | 'api.chat.getChatMembers'
  | 'api.deepLinks.executeDeepLink'
  | 'api.deepLinks.shareDeepLink'
  | 'api.dialogs.open'
  | 'api.dialogs.resize'
  | 'api.dialogs.submit'
  | 'api.files.openFilePreview'
  | 'api.files.getCloudStorageFolders'
  | 'api.files.addCloudStorageFolder'
  | 'api.files.deleteCloudStorageFolder'
  | 'api.getAuthToken'
  | 'api.getContext'
  | 'api.getUser'
  | 'api.location.getLocation'
  | 'api.location.showLocation'
  | 'api.mail.composeMail'
  | 'api.mail.openMailItem'
  | 'api.media.captureImage'
  | 'api.media.getMedia'
  | 'api.media.scanBarCode'
  | 'api.media.selectMedia'
  | 'api.media.viewImages'
  | 'api.meeting.getAuthenticationTokenForAnonymousUser'
  | 'api.meeting.getIncomingClientAudioState'
  | 'api.meeting.getMeetingDetails'
  | 'api.meeting.toggleIncomingClientAudio'
  | 'api.notifications.showNotification'
  | 'api.pages.config.getConfig'
  | 'api.pages.config.setConfig'
  | 'api.pages.config.remove.failure'
  | 'api.pages.config.remove.success'
  | 'api.pages.config.save.failure'
  | 'api.pages.config.save.success'
  | 'api.pages.config.setValidityState'
  | 'api.pages.backStack.navigateBack'
  | 'api.pages.fullTrust.enterFullscreen'
  | 'api.pages.fullTrust.exitFullscreen'
  | 'api.pages.navigateCrossDomain'
  | 'api.pages.returnFocus'
  | 'api.pages.tabs.getMruTabInstances'
  | 'api.pages.tabs.getTabInstances'
  | 'api.pages.tabs.navigateToTab'
  | 'api.people.selectPeople'
  | 'api.registerHandler'
  | 'api.teams.fullTrust.getUserJoinedTeams'
  | 'api.teams.fullTrust.getConfigSetting'
  | 'eventDispatch.themeChange'
  | 'eventDispatch.userSettingsChange'
  | 'eventDispatch.log.request'
  | 'eventDispatch.fullScreenChange'
  | 'eventDispatch.appButtonClick'
  | 'eventDispatch.appButtonHoverEnter'
  | 'eventDispatch.appButtonHoverLeave'
  | 'eventDispatch.changeSettings'
  | 'eventDispatch.settings.save'
  | 'eventDispatch.settings.remove'
  | 'eventDispatch.backButtonPress';

export const messageToScenarioMap: {
  [message: string]: TelemetryScenarioName;
} = {
  [SdkMessageTypes.appInstallDialog.openAppInstallDialog]: 'api.appInstallDialog.openAppInstallDialog',
  [SdkMessageTypes.authentication.getAuthToken]: 'api.getAuthToken',
  [SdkMessageTypes.authentication.getUser]: 'api.getUser',
  [SdkMessageTypes.authentication.notifyFailure]: 'api.authentication.notifyFailure',
  [SdkMessageTypes.authentication.notifySuccess]: 'api.authentication.notifySuccess',
  [SdkMessageTypes.appEntity.selectAppEntity]: 'api.appEntity.selectAppEntity',
  [SdkMessageTypes.calendar.composeMeeting]: 'api.calendar.composeMeeting',
  [SdkMessageTypes.calendar.openCalendarItem]: 'api.calendar.openCalendarItem',
  [SdkMessageTypes.call.startCall]: 'api.call.startCall',
  [SdkMessageTypes.chat.openConversation]: 'api.chat.openConversation',
  [SdkMessageTypes.chat.closeConversation]: 'api.chat.closeConversation',
  [SdkMessageTypes.chat.getChatMembers]: 'api.chat.getChatMembers',
  [SdkMessageTypes.deepLinks.executeDeepLink]: 'api.deepLinks.executeDeepLink',
  [SdkMessageTypes.deepLinks.shareDeepLink]: 'api.deepLinks.shareDeepLink',
  [SdkMessageTypes.dialogs.open]: 'api.dialogs.open',
  [SdkMessageTypes.dialogs.resize]: 'api.dialogs.resize',
  [SdkMessageTypes.dialogs.submit]: 'api.dialogs.submit',
  [SdkMessageTypes.files.openFilePreview]: 'api.files.openFilePreview',
  [SdkMessageTypes.files.getCloudStorageFolders]: 'api.files.getCloudStorageFolders',
  [SdkMessageTypes.files.addCloudStorageFolder]: 'api.files.addCloudStorageFolder',
  [SdkMessageTypes.files.deleteCloudStorageFolder]: 'api.files.deleteCloudStorageFolder',
  [SdkMessageTypes.getContext]: 'api.getContext',
  [SdkMessageTypes.location.getLocation]: 'api.location.getLocation',
  [SdkMessageTypes.location.showLocation]: 'api.location.showLocation',
  [SdkMessageTypes.mail.openMailItem]: 'api.mail.openMailItem',
  [SdkMessageTypes.mail.composeMail]: 'api.mail.composeMail',
  [SdkMessageTypes.media.captureImage]: 'api.media.captureImage',
  [SdkMessageTypes.media.getMedia]: 'api.media.getMedia',
  [SdkMessageTypes.media.scanBarCode]: 'api.media.scanBarCode',
  [SdkMessageTypes.media.selectMedia]: 'api.media.selectMedia',
  [SdkMessageTypes.media.viewImages]: 'api.media.viewImages',
  [SdkMessageTypes.meeting.getAuthenticationTokenForAnonymousUser]:
    'api.meeting.getAuthenticationTokenForAnonymousUser',
  [SdkMessageTypes.meeting.getIncomingClientAudioState]: 'api.meeting.getIncomingClientAudioState',
  [SdkMessageTypes.meeting.getMeetingDetails]: 'api.meeting.getMeetingDetails',
  [SdkMessageTypes.meeting.toggleIncomingClientAudio]: 'api.meeting.toggleIncomingClientAudio',
  [SdkMessageTypes.notifications.showNotification]: 'api.notifications.showNotification',
  [SdkMessageTypes.pages.config.getConfig]: 'api.pages.config.getConfig',
  [SdkMessageTypes.pages.config.setConfig]: 'api.pages.config.setConfig',
  [SdkMessageTypes.pages.config.remove.failure]: 'api.pages.config.remove.failure',
  [SdkMessageTypes.pages.config.remove.success]: 'api.pages.config.remove.success',
  [SdkMessageTypes.pages.config.save.failure]: 'api.pages.config.save.failure',
  [SdkMessageTypes.pages.config.save.success]: 'api.pages.config.save.success',
  [SdkMessageTypes.pages.config.setValidityState]: 'api.pages.config.setValidityState',
  [SdkMessageTypes.pages.backStack.navigateBack]: 'api.pages.backStack.navigateBack',
  [SdkMessageTypes.pages.fullTrust.enterFullscreen]: 'api.pages.fullTrust.enterFullscreen',
  [SdkMessageTypes.pages.fullTrust.exitFullscreen]: 'api.pages.fullTrust.exitFullscreen',
  [SdkMessageTypes.pages.navigateCrossDomain]: 'api.pages.navigateCrossDomain',
  [SdkMessageTypes.pages.returnFocus]: 'api.pages.returnFocus',
  [SdkMessageTypes.pages.tabs.getMruTabInstances]: 'api.pages.tabs.getMruTabInstances',
  [SdkMessageTypes.pages.tabs.getTabInstances]: 'api.pages.tabs.getTabInstances',
  [SdkMessageTypes.pages.tabs.navigateToTab]: 'api.pages.tabs.navigateToTab',
  [SdkMessageTypes.people.selectPeople]: 'api.people.selectPeople',
  [SdkMessageTypes.registerHandler]: 'api.registerHandler',
  [SdkMessageTypes.teams.fullTrust.getUserJoinedTeams]: 'api.teams.fullTrust.getUserJoinedTeams',
  [SdkMessageTypes.teams.fullTrust.getConfigSetting]: 'api.teams.fullTrust.getConfigSetting',
};

export type TelemetryPhaseName = 'ClickToBeginLoad' | 'BeginLoadToInitialized' | 'MessageDelay';

/**
 * Interface for Telemetry Scenario
 */
export interface ITelemetryScenario {
  /**
   * Marks an intermediate point in the overall scenario timeline
   */
  markPhase(name: TelemetryPhaseName): void;
  /**
   * Marks the completion of this particular scenario
   */
  stop(): void;
  /**
   * Marks a failure of this scenario to complete
   * @param error error information
   */
  fail(error?: string): void;
}

export interface ITelemetryService {
  /**
   * Initializing a telemetry scenario based on the named API
   * @param name name of API scenario that is being run
   * @param appId appId of the hosted application
   * @param clientTimestamp timestamp for when the message was created by the App SDK
   */
  createScenario(name: TelemetryScenarioName, appId?: string, clientTimestamp?: number): ITelemetryScenario;
}
